/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/display-name */
import React, {
  memo,
  JSX,
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import ItemsScrollBar from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsScrollBar';
import useInView from '@wconcept/wconcept-ui-renewal/hooks/useInView';
import * as S from './style';

interface IProps {
  children?: JSX.Element | JSX.Element[];
}

interface VideoChildProps {
  isActive?: boolean;
  itemRef?: React.RefObject<HTMLDivElement | null>;
  videoPoster?: string;
}

interface ObservedItemProps {
  children: React.ReactElement<VideoChildProps>;
  shouldObserve: boolean;
  index: number;
  onVisibilityChange: (index: number, isVisible: boolean) => void;
  activeVideoIndex: number | null;
}

// ObservedItem 컴포넌트 메모이제이션
const ObservedItem = memo(
  ({
    children,
    shouldObserve,
    index,
    onVisibilityChange,
    activeVideoIndex,
  }: ObservedItemProps) => {
    const [ref, isVisible] = useInView(
      useMemo(
        () => ({
          root: null,
          rootMargin: '0px',
          threshold: 0.7,
        }),
        []
      ),
      shouldObserve
    );

    const { videoPoster, videoSrc } = children.props as any;
    const hasVideo = videoPoster || videoSrc;

    useEffect(() => {
      if (hasVideo) {
        onVisibilityChange(index, isVisible);
      }
    }, [isVisible, hasVideo, index, onVisibilityChange]);

    const isActive =
      isVisible && (hasVideo ? index === activeVideoIndex : true);

    return <>{React.cloneElement<any>(children, { isActive, itemRef: ref })}</>;
  }
);

// RecoStylingList 컴포넌트 내부
const RecoStylingList = ({ children }: IProps) => {
  const [shouldObserve, setShouldObserve] = useState(false);
  const [visibleVideoIndices, setVisibleVideoIndices] = useState<Set<number>>(
    new Set()
  );

  // 가장 좌측에 보이는 비디오 아이템의 인덱스를 찾는 함수
  const getLeftmostVideoIndex = useMemo(() => {
    if (visibleVideoIndices.size === 0) return null;

    const childrenArray = Array.isArray(children) ? children : [children];
    const sortedIndices = Array.from(visibleVideoIndices).sort((a, b) => a - b);

    // 가장 좌측부터 순서대로 확인하여 첫 번째 비디오 아이템 찾기
    for (const index of sortedIndices) {
      const child = childrenArray[index];
      if (
        child &&
        child.props &&
        (child.props.videoPoster || child.props.videoSrc)
      ) {
        return index;
      }
    }

    return null;
  }, [visibleVideoIndices, children]);

  // 최초 실행
  useEffect(() => {
    setTimeout(() => {
      setShouldObserve(true);
    }, 100);
  }, []);

  const handleTouchStart = useCallback(() => {
    // 즉시 Observer 비활성화
    setShouldObserve(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    // 터치 종료 후 약간의 지연을 두고 Observer 재활성화
    setTimeout(() => {
      setShouldObserve(true);
    }, 100);
  }, []);

  const handleVisibilityChange = useCallback(
    (index: number, isVisible: boolean) => {
      setVisibleVideoIndices(prev => {
        const newSet = new Set(prev);
        if (isVisible) {
          newSet.add(index);
        } else {
          newSet.delete(index);
        }
        return newSet;
      });
    },
    []
  );

  const getListRender = useCallback(
    (el: React.ReactNode) => {
      if (Array.isArray(el)) {
        return el
          .map((v: React.ReactElement, i) => {
            if (v) {
              return (
                <ObservedItem
                  key={v.key || `item-${i}`}
                  shouldObserve={shouldObserve}
                  index={i}
                  onVisibilityChange={handleVisibilityChange}
                  activeVideoIndex={getLeftmostVideoIndex}
                >
                  {v as React.ReactElement<VideoChildProps>}
                </ObservedItem>
              );
            }
            return null;
          })
          .filter((item): item is React.ReactElement => item !== null);
      }
      return null;
    },
    [getLeftmostVideoIndex, shouldObserve, handleVisibilityChange]
  );

  return (
    <S.RecoStylingList>
      <ItemsScrollBar
        snap
        perView={2.991}
        offsetLR={20}
        colGap={6}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {getListRender(children)}
      </ItemsScrollBar>
    </S.RecoStylingList>
  );
};

export default memo(RecoStylingList);
